// src/data/useFxRates.js
//
// LSE-TAB-REDESIGN Part 1 — React hook returning the latest FX rates
// keyed by 3-letter currency code, suitable for direct consumption by
// fxConvert.convertAmount().
//
// Cache model: ONE in-module promise per session. Explicit basis writes
// invalidate it through cacheBus so mounted consumers refresh together.
//
// Shape returned:
//   { rates, loading, error }
//   rates    : { USD: 1, GBP: 0.792, EUR: 0.920, ... } | null while loading
//   loading  : boolean
//   error    : Error | null
//
// `rates` is null until the first fetch resolves. Callers (currency
// toggle, money cells) MUST tolerate a brief null window — they
// either render the native value (USD short-circuit in convertAmount)
// or show a tiny "—" placeholder.

import { useEffect, useState } from "react";
import { log } from "../utils/logger.js";

let _ratesPromise = null;
let _ratesFetchedAt = 0;
if (typeof window !== "undefined") {
  window.addEventListener("ripple:fx-rates-invalidated", () => {
    _ratesPromise = null;
    _ratesFetchedAt = 0;
  });
  window.addEventListener("focus", () => {
    if (_ratesFetchedAt && Date.now() - _ratesFetchedAt >= 60_000) {
      _ratesPromise = null;
      _ratesFetchedAt = 0;
      window.dispatchEvent(new Event("ripple:fx-rates-invalidated"));
    }
  });
}

function fetchRates() {
  if (_ratesPromise) return _ratesPromise;
  _ratesPromise = fetch("/api/fx-rates", {
    credentials: "include",
    headers: { "Content-Type": "application/json" },
  })
    .then((res) => {
      if (!res.ok) throw new Error(`fx-rates HTTP ${res.status}`);
      return res.json();
    })
    .then((body) => {
      // basisRates is resolved by the server against the saved workspace
      // basis; `rates` remains the legacy admin-panel history contract.
      const arr = Array.isArray(body?.basisRates) ? body.basisRates : (Array.isArray(body?.rates) ? body.rates : []);
      const map = {};
      for (const r of arr) {
        const rate = Number(r.rateToUsd);
        if (typeof r.currency === "string" && Number.isFinite(rate) && rate > 0) {
          map[r.currency] = rate;
        }
      }
      // USD baseline always 1 (defensive — server seed guarantees this,
      // but if the row is ever missing the hook should still work).
      if (!Number.isFinite(map.USD)) map.USD = 1;
      _ratesFetchedAt = Date.now();
      return map;
    })
    .catch((err) => {
      // Reset the promise so a retry-on-remount will re-fire. Without
      // this, a transient network error would permanently lock the
      // page into a "no rates" state until reload.
      _ratesPromise = null;
      _ratesFetchedAt = 0;
      log.error("useFxRates fetch failed", { err: String(err) });
      throw err;
    });
  return _ratesPromise;
}

// Test-only escape hatch — call between tests to clear the module-level
// promise cache so each test sees a fresh fetch. Not exported via the
// barrel; tests import it explicitly.
export function __resetFxRatesCache() {
  _ratesPromise = null;
  _ratesFetchedAt = 0;
}

export function useFxRates() {
  const [rates, setRates]     = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);

  useEffect(() => {
    let cancelled = false;
    const applyFreshRates = (map) => {
      if (cancelled) return;
      setRates(map);
      setLoading(false);
      setError(null);
    };
    const refresh = () => {
      setLoading(true);
      fetchRates().then(applyFreshRates).catch((err) => {
        if (cancelled) return;
        setError(err);
        setLoading(false);
      });
    };
    setLoading(true);
    setError(null);
    fetchRates()
      .then(applyFreshRates)
      .catch((err) => {
        if (cancelled) return;
        setError(err);
        setLoading(false);
      });
    window.addEventListener("ripple:fx-rates-invalidated", refresh);
    return () => {
      cancelled = true;
      window.removeEventListener("ripple:fx-rates-invalidated", refresh);
    };
  }, []);

  return { rates, loading, error };
}
